import React from 'react';
import { Shield, Users, Award, Target, Heart, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import PopupButton from '../components/PopupButton';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const AboutUs = () => {
  const values = [
    {
      icon: Shield,
      title: 'Trust & Security',
      description: 'We prioritize the security of your intellectual property and maintain the highest standards of confidentiality.'
    },
    {
      icon: Users,
      title: 'Client-Focused',
      description: 'Your success is our success. We provide personalized attention and support throughout your trademark journey.'
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'We deliver expert-level trademark services with precision, ensuring compliance and successful registration.'
    },
    {
      icon: Target,
      title: 'Results-Driven',
      description: 'Our streamlined process is designed to get your trademark registered efficiently and effectively.'
    }
  ];

  const stats = [
    { number: '10,000+', label: 'Trademarks Registered' },
    { number: '98%', label: 'Success Rate' },
    { number: '50+', label: 'States Served' },
    { number: '24/7', label: 'Customer Support' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="About Assess Trademark | Trusted Trademark Experts"
        description="Learn about Assess Trademark and our commitment to helping businesses protect their brands through trademark, copyright, and business registration services."
      />
      {/* Banner */}
      <section className="py-12 bg-primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold">About Assess Trademark</h1>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-primary mb-2">
                  {stat.number}
                </div>
                <p className="text-gray-600 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-lg text-gray-600">
                <p>
                  Assess Trademark was founded with a simple yet powerful mission: to make trademark protection accessible, affordable, and stress-free for businesses of all sizes. We recognized that many entrepreneurs and business owners struggled with the complex legal processes of trademark registration, often facing unnecessary delays, rejections, and confusion.
                </p>
                <p>
                  With years of expertise in intellectual property law and a passion for helping businesses succeed, our team set out to create a streamlined, transparent, and customer-focused trademark service. Today, we've helped thousands of businesses across the United States secure their brand identity and protect what matters most to them.
                </p>
                <p>
                  We believe that every business—big or small—deserves strong legal protection. That's why we offer a smooth, user-friendly experience backed by trademark specialists who genuinely care about your brand's future.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="w-full h-[500px] bg-blue-100 rounded-2xl flex items-center justify-center p-4">
                <img 
                  src="/assets/img/Our-Story-about-us.svg" 
                  alt="Our Story and Mission Illustration"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Target className="w-7 h-7 text-primary" />
                </div>
                <CardTitle className="text-3xl">Our Mission</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-lg text-gray-600">
                  To empower businesses with accessible, reliable, and expert trademark registration services that protect their brand identity and foster growth. We're committed to simplifying the complex legal process while maintaining the highest standards of accuracy and compliance.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <TrendingUp className="w-7 h-7 text-primary" />
                </div>
                <CardTitle className="text-3xl">Our Vision</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-lg text-gray-600">
                  To become the most trusted and innovative trademark service provider in the United States, recognized for our commitment to client success, transparency, and excellence. We envision a future where every business can easily protect their intellectual property without barriers.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <p className="text-lg text-gray-600">
              The principles that guide everything we do
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <CardHeader>
                    <div className="mx-auto w-16 h-16 bg-primary rounded-full flex items-center justify-center mb-4">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <CardTitle>{value.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Businesses Choose Assess Trademark
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We combine legal expertise with cutting-edge technology to deliver trademark services that are fast, reliable, and affordable.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-10 h-10 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Expert Team</h3>
              <p className="text-gray-600">
                Our experienced trademark specialists have years of expertise in intellectual property law and USPTO regulations.
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-10 h-10 text-cyan-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Proven Process</h3>
              <p className="text-gray-600">
                Our streamlined process has successfully helped thousands of businesses protect their brand identity.
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-10 h-10 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Dedicated Support</h3>
              <p className="text-gray-600">
                Get personalized support from real people who care about your success every step of the way.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Ready to Protect Your Brand?
          </h2>
          <p className="text-lg md:text-xl mb-8 opacity-90">
            Join thousands of satisfied businesses who trust us with their trademark protection.
          </p>
          <div className="flex justify-center gap-4">
            <Link to="/trademark-registration">
              <PopupButton size="lg" variant="secondary" className="bg-white text-primary hover:bg-gray-100">
                Get Started Today
              </PopupButton>
            </Link>
            <Link to="/contact-us">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
